import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../common/utils';
import { Button } from '../../components/actions/Button';
import DropdownSelector from '../../components/actions/DropdownSelector';
import * as Icon from '../../components/icons';
import DateRangePicker, {
  ShiftTypeConfig,
  createShiftSelectorConfig,
} from '../../components/inputs/calendar/DateRangePicker';
import InputTime from '../../components/inputs/calendar/InputTime';
import InputText from '../../components/inputs/InputText';
import TitleSection from '../../components/layout/TitleSection';
import { ActionBar } from '../../components/nav/ActionBar';
import PanelContainer from '../../containers/PanelContainer';
import useDatePickerSettings from '../hooks/useDatePickerSettings';

const DatePickerSettingsPanel: React.FC = () => {
  const { t } = useTranslation('feed');

  // Use warehouse-specific settings
  const {
    settings,
    updateSettings,
    saveSettings: saveToWarehouse,
  } = useDatePickerSettings();
  const [editingShiftType, setEditingShiftType] =
    useState<ShiftTypeConfig | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const pendingEditRef = useRef<string | null>(null);

  // Debug effect to track state changes
  useEffect(() => {
    console.log('🔄 DatePickerSettingsPanel state:', {
      editingShiftTypeId: editingShiftType?.id,
      isAddingNew,
      pendingEdit: pendingEditRef.current,
      shiftsCount: settings.shiftTypes.length,
    });
  }, [editingShiftType, isAddingNew, settings.shiftTypes.length]);

  // Effect to restore editing state after settings update
  useEffect(() => {
    if (pendingEditRef.current) {
      const shiftToEdit = settings.shiftTypes.find(
        st => st.id === pendingEditRef.current,
      );
      if (shiftToEdit) {
        console.log('🔄 Restoring editing state for:', shiftToEdit.id);
        setEditingShiftType(shiftToEdit);
        pendingEditRef.current = null;
      }
    }
  }, [settings.shiftTypes]);

  const updateShiftType = (id: string, updates: Partial<ShiftTypeConfig>) => {
    const updatedShiftTypes = settings.shiftTypes.map(st =>
      st.id === id ? { ...st, ...updates } : st,
    );
    updateSettings({ shiftTypes: updatedShiftTypes });

    // Update the editing state as well
    if (editingShiftType?.id === id) {
      setEditingShiftType({ ...editingShiftType, ...updates });
    }
  };

  const addShiftType = () => {
    console.log('🔄 Adding new shift type...');

    const newShiftType: ShiftTypeConfig = {
      id: `custom_${Date.now()}`,
      name: 'New Shift',
      startHour: 8,
      startMinute: 0,
      endHour: 16,
      endMinute: 0,
      color: '#6B7280',
    };

    console.log('📝 New shift type created:', newShiftType);

    try {
      // Set pending edit to track which shift to edit after settings update
      pendingEditRef.current = newShiftType.id;
      setIsAddingNew(true);

      // Update settings first, editing state will be set by useEffect
      updateSettings({
        shiftTypes: [...settings.shiftTypes, newShiftType],
      });

      console.log('✅ Shift type added successfully');
    } catch (error) {
      console.error('❌ Error adding shift type:', error);
      pendingEditRef.current = null;
      setIsAddingNew(false);
      setEditingShiftType(null);
    }
  };

  const removeShiftType = (id: string) => {
    const updatedShiftTypes = settings.shiftTypes.filter(st => st.id !== id);
    updateSettings({ shiftTypes: updatedShiftTypes });
    if (editingShiftType?.id === id) {
      setEditingShiftType(null);
      setIsAddingNew(false);
    }
  };

  const resetToDefaults = () => {
    updateSettings({
      enableShiftTypeSelectors: false,
      maxShiftsToShow: 5,
      shiftTypes: [],
      enableRelativeTimeSelectors: true,
      enableAbsoluteTimeSelectors: true,
    });
    pendingEditRef.current = null;
    setEditingShiftType(null);
    setIsAddingNew(false);
  };

  const saveSettings = async () => {
    // Manually save settings to warehouse
    await saveToWarehouse();
    console.log('DatePicker settings saved to warehouse:', settings);
    pendingEditRef.current = null;
    setEditingShiftType(null);
    setIsAddingNew(false);
  };

  const cancelEdit = () => {
    if (isAddingNew && editingShiftType) {
      // Remove the newly added shift type if canceling
      removeShiftType(editingShiftType.id);
    }
    pendingEditRef.current = null;
    setEditingShiftType(null);
    setIsAddingNew(false);
  };

  const formatTimeRange = (
    startHour: number,
    startMinute: number = 0,
    endHour: number,
    endMinute: number = 0,
  ) => {
    const formatTime = (hour: number, minute: number) => {
      const displayHour = hour > 23 ? hour - 24 : hour;
      return `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    };

    const start = formatTime(startHour, startMinute);
    const end = formatTime(endHour, endMinute);
    return `${start} - ${end}`;
  };

  // Helper functions for time conversion
  const formatTimeFromHoursMinutes = (hours: number, minutes: number) => {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const parseTimeToHoursMinutes = (timeString: string) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return { hours: hours || 0, minutes: minutes || 0 };
  };

  const setStartTime = (timeString: string) => {
    if (!editingShiftType) return;
    const { hours, minutes } = parseTimeToHoursMinutes(timeString);
    updateShiftType(editingShiftType.id, {
      startHour: hours,
      startMinute: minutes,
    });
  };

  const setEndTime = (timeString: string) => {
    if (!editingShiftType) return;
    const { hours, minutes } = parseTimeToHoursMinutes(timeString);
    updateShiftType(editingShiftType.id, {
      endHour: hours,
      endMinute: minutes,
    });
  };

  // Create dropdown menu options
  const dropOptions = _.keyBy(
    [
      {
        key: 'enableShiftTypeSelectors',
        title: settings.enableShiftTypeSelectors
          ? t`Disable Shifts`
          : t`Enable Shifts`,
        // icon: <Icon.Time className="h-4 w-4 fill-current" />,
        status: settings.enableShiftTypeSelectors ? 'on' : 'off',
        onClick: () =>
          updateSettings({
            enableShiftTypeSelectors: !settings.enableShiftTypeSelectors,
          }),
      },
      {
        key: 'enableRelativeTimeSelectors',
        title: settings.enableRelativeTimeSelectors
          ? t`Disable Relative Time`
          : t`Enable Relative Time`,
        // icon: <Icon.Calendar className="h-4 w-4 fill-current" />,
        status: settings.enableRelativeTimeSelectors ? 'on' : 'off',
        onClick: () =>
          updateSettings({
            enableRelativeTimeSelectors: !settings.enableRelativeTimeSelectors,
          }),
      },
      {
        key: 'divider',
        title: '---',
        icon: null,
        status: '',
        onClick: () => {},
      },
      {
        key: 'resetToDefaults',
        title: t`Reset to Defaults`,
        icon: <Icon.Refresh className="h-4 w-4 fill-current" />,
        status: '',
        onClick: () => resetToDefaults(),
      },
    ],
    'key',
  );

  return (
    <PanelContainer
      id="panel-datepicker-shift-types"
      title={t`Shift Display Configuration`}
      collapsible
      classNameChildren="p-2"
    >
      {/* General Settings */}

      {/* Shift Types Configuration */}

      <TitleSection
        id="shift-types"
        title={t`Shift Date Picker settings`}
        inPanelView
        hasAction={
          <>
            <DropdownSelector
              value="settings"
              values={_.keys(dropOptions)}
              renderValue={(key: string) => {
                const option = dropOptions[key as string];
                if (!option) return null;
                if (key === 'divider') {
                  return (
                    <div className="border-menu-active/20 my-1 border-t" />
                  );
                }
                return (
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                      {option.icon}
                      <span className="ml-2">{option.title}</span>
                    </div>
                    {option.status && (
                      <span
                        className={`text-xs ${
                          option.status === 'on'
                            ? 'text-menu-active'
                            : 'text-menu-text/50'
                        }`}
                      >
                        {option.status === 'on' ? t`On` : t`Off`}
                      </span>
                    )}
                  </div>
                );
              }}
              renderValueSelected={() => (
                <div className="flex items-center">{t`Configure`}</div>
              )}
              onChange={(key: string) => {
                const option = dropOptions[key as string];
                if (option && _.isFunction(option.onClick)) {
                  option.onClick();
                }
              }}
              buttonTransparent
              vertical
              panelMode
              className="rounded"
            />
          </>
        }
        // classNameInner="p-0.5 xl:p-1"
      >
        {/* Add New Shift Type */}
        <ActionBar className="flex items-center justify-between p-2">
          <Button
            onPress={() => {
              console.log('🖱️ Add button clicked, current state:', {
                isAddingNew,
                shiftsCount: settings.shiftTypes.length,
                editingShiftType: editingShiftType?.id,
              });
              addShiftType();
            }}
            buttonType="primary"
            buttonSize="xs"
            hasIconBefore
            buttonIcon={<Icon.Plus className="h-4 w-4 fill-current" />}
            label={t`Add Shift`}
            isDisabled={isAddingNew}
            className="rounded"
          />

          {isAddingNew && (
            <div className="text-menu-text/60 text-sm">
              {t`Adding new shift...`}
            </div>
          )}
        </ActionBar>

        <div className="space-y-4">
          {/* DateRangePicker Preview */}
          <div className="space-y-2">
            <div className="bg-app-panel/30 rounded p-2">
              <DateRangePicker
                value={[new Date(), new Date()]}
                onChange={() => {}} // Preview only, no actual changes
                label={t`Date Range Preview`}
                showTimeMenu={true}
                showDateMenu={true}
                shiftConfig={createShiftSelectorConfig({
                  shifts: [], // No actual shift data for preview
                  maxShiftsToShow: settings.maxShiftsToShow,
                  enableShiftSelectors: false, // Disable individual shifts
                  enableShiftTypeSelectors: settings.enableShiftTypeSelectors,
                  shiftTypes: settings.shiftTypes,
                })}
              />
              {/* Debug info for preview */}
              {process.env.NODE_ENV === 'development' &&
                settings.shiftTypes.length > 0 && (
                  <div className="text-menu-text/60 mt-2 text-xs">
                    <div>
                      Preview will show {settings.shiftTypes.length} shift type
                      selectors:
                    </div>
                    {settings.shiftTypes.map(st => (
                      <div key={st.id} className="ml-2">
                        • {st.name} ({st.startHour}:00-{st.endHour}:00)
                      </div>
                    ))}
                  </div>
                )}
              {/* <div className="text-menu-text/60 mt-2 text-xs">
                {settings.shiftTypes.length > 0
                  ? t`Click the calendar button to see your configured shifts`
                  : t`Add shift types above to see them in the date picker`}
              </div> */}
            </div>
          </div>

          {/* Shift Types List */}
          <ul className="flex flex-col gap-2 pb-4">
            {settings.shiftTypes.map(shiftType => (
              <li
                key={shiftType.id}
                className={`rounded transition-colors ${
                  editingShiftType?.id === shiftType.id
                    ? 'border-menu-active bg-menu text-menu-text'
                    : 'border-menu-active/30 hover:border-menu-active/50'
                } `}
              >
                {editingShiftType?.id === shiftType.id ? (
                  /* Edit Mode */
                  <div className="flex flex-col gap-4 p-2">
                    <div className="text-menu-text flex gap-1 text-xs">
                      <span>{t`Shift Range:`}</span>
                      <span>{`${formatTimeRange(editingShiftType.startHour, editingShiftType.startMinute, editingShiftType.endHour, editingShiftType.endMinute)}`}</span>
                      {editingShiftType.endHour > 23 && (
                        <span className="ml-2 text-yellow-500">{t`(Overnight shift)`}</span>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="w-full">
                        <InputText
                          // label={t`Shift Name`}
                          value={editingShiftType.name}
                          onChange={value =>
                            updateShiftType(shiftType.id, { name: value })
                          }
                          placeholder="Enter shift name"
                          className="w-full border-none !p-2 text-sm"
                          classNameLabel="text-xxs uppercase"
                        />
                      </div>

                      <div className="mb-2 flex flex-1 flex-col px-1 py-1 xl:px-3">
                        <div
                          data-component="FromToTimeSelect"
                          className="mt-2 flex flex-1 space-x-2"
                        >
                          <InputTime
                            label={t`From`}
                            value={formatTimeFromHoursMinutes(
                              editingShiftType.startHour,
                              editingShiftType.startMinute || 0,
                            )}
                            onChange={setStartTime}
                          />
                          <InputTime
                            label={t`To`}
                            value={formatTimeFromHoursMinutes(
                              editingShiftType.endHour,
                              editingShiftType.endMinute || 0,
                            )}
                            onChange={setEndTime}
                          />
                        </div>
                      </div>
                    </div>

                    <ActionBar
                      transparent
                      className="mt-4 flex justify-end gap-2"
                    >
                      <Button
                        onPress={cancelEdit}
                        buttonType="secondary"
                        buttonSize="xs"
                        label={t`Cancel`}
                      />
                      <Button
                        onPress={saveSettings}
                        buttonType="primary"
                        buttonSize="xs"
                        label={t`Save`}
                      />
                    </ActionBar>
                  </div>
                ) : (
                  /* Display Mode */

                  <div
                    data-component="ShiftItem"
                    // name={shiftType.name}
                    // value={formatTimeRange(
                    //   shiftType.startHour,
                    //   shiftType.startMinute,
                    //   shiftType.endHour,
                    //   shiftType.endMinute,
                    // )}
                    className="bg-app-panel/50 flex flex-col items-center justify-between gap-2 rounded p-2"
                  >
                    <div className="flex w-full items-center gap-2">
                      <div
                        title={shiftType.name}
                        className="text-menu-text flex-1 truncate"
                      >
                        {shiftType.name}
                      </div>
                      <div className="text-menu-text/60">
                        {formatTimeRange(
                          shiftType.startHour,
                          shiftType.startMinute,
                          shiftType.endHour,
                          shiftType.endMinute,
                        )}
                      </div>
                    </div>
                    <ActionBar
                      transparent
                      className="flex w-full items-center justify-end gap-2"
                    >
                      <Button
                        onPress={() => setEditingShiftType(shiftType)}
                        buttonType="secondary"
                        buttonSize="xs"
                        className="justify-start rounded-full p-2"
                        label={t`Edit`}
                      />

                      <Button
                        onPress={() => removeShiftType(shiftType.id)}
                        buttonType="ghost"
                        buttonSize="xs"
                        className="bg-menu-delete justify-end rounded-full p-2"
                        hasIconBefore={
                          <Icon.Close
                            className={cn('h-3 w-3', 'mx-1', 'fill-current')}
                          />
                        }
                        // label={t`Delete`}
                      />
                    </ActionBar>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </TitleSection>
    </PanelContainer>
  );
};

export default DatePickerSettingsPanel;
